import type { CombinedError } from '@urql/core';
import type { UnifiedAuthStep } from '~kui/types/auth';
import type { Ref } from 'vue';

import type { AuthAnalyticsMethodValue } from '~/constants/auth';
import type { SelectedEntityInput, SiteRoleType } from '~/graphql/generated/graphql';
import type { CompletedFlowResult, SignInContinuation } from '~/types/login';

import { AuthAnalyticsMethod } from '~/constants/auth';
import { LoginFlowType } from '~/graphql/generated/graphql';

import type { UnifiedAuthState } from './useUnifiedAuthFlow';

interface AuthPasswordLoginOptions {
  roleType: SiteRoleType;
  authState: Ref<UnifiedAuthState>;
  loading: Ref<boolean>;
  goToStep: (step: UnifiedAuthStep) => void;
  /** The arrival entity from the login URL, sent so the server keeps it the way the OTP path does. */
  selectedEntity: SelectedEntityInput | null;
  sendLoginCode: () => Promise<{ error?: CombinedError | null }>;
  /** Where a passed credential leads, owned by the flow: signed in, signed up, or a step still owed. */
  continueSignIn: (result: SignInContinuation, method: AuthAnalyticsMethodValue) => Promise<CompletedFlowResult>;
  /** Back to the email step with the reason, for a sign-in the server no longer holds. */
  restartSignIn: (message: string) => void;
}

/**
 * The password half of the unified auth flow: the method-choice handlers and the
 * password submit, split out the way `useSocialAuthCallback` extracts the social
 * concern. Where an accepted password leads stays with the owner flow.
 */
export const useAuthPasswordLogin = ({
  roleType,
  authState,
  loading,
  goToStep,
  selectedEntity,
  sendLoginCode,
  continueSignIn,
  restartSignIn,
}: AuthPasswordLoginOptions) => {
  const { t } = useI18n();
  const { $analytics } = useNuxtApp();
  const { loginWithPassword } = useAuth();
  const { resetPasswordAfterCode } = useSchoolsSignIn();
  const configStore = useConfigStore();

  const sendCodeAndGoToOtp = async () => {
    const requestedFor = authState.value;
    loading.value = true;
    const { error } = await sendLoginCode();
    loading.value = false;
    // A reset while the code was in flight replaces the auth state, so the reply belongs to an abandoned attempt.
    if (authState.value !== requestedFor) {
      return { error: null, superseded: true };
    }
    if (error) {
      return { error, superseded: false };
    }
    goToStep('otp');
    return { error: null, superseded: false };
  };

  const chooseOtpMethod = async () => {
    const result = await sendCodeAndGoToOtp();
    if (!result.error && !result.superseded) {
      $analytics.track(EVENTS.LOGIN_METHOD_CHOSEN, { method: AuthAnalyticsMethod.EmailOtp });
    }
    return result;
  };

  const choosePasswordMethod = () => {
    $analytics.track(EVENTS.LOGIN_METHOD_CHOSEN, { method: AuthAnalyticsMethod.EmailPassword });
    goToStep('password');
  };

  /**
   * The schools site asked for the code before the password, so a forgotten one is reset rather
   * than swapped for another code: the server signs in on the code already given and hands back a
   * token for the new password. One whose code has lapsed starts the sign-in over.
   */
  const resetForgottenPassword = async (): Promise<CompletedFlowResult | { flow: null; error: CombinedError | string }> => {
    loading.value = true;

    const { flow, hasMatchedInvite, passwordChangeToken, error } = await resetPasswordAfterCode({
      email: authState.value.email,
      roleType,
      ...(selectedEntity ? { selectedEntity } : {}),
    });

    if (!flow) {
      loading.value = false;
      return { flow: null, error };
    }

    if (flow === LoginFlowType.SignInExpired || flow === LoginFlowType.InvalidCredentials) {
      loading.value = false;
      restartSignIn(t('schools.auth.signInExpired'));
      return { flow, error: null };
    }

    return await continueSignIn({ flow, hasMatchedInvite, passwordChangeToken }, AuthAnalyticsMethod.EmailOtp);
  };

  const forgotPassword = () => (configStore.isSchoolsCluster ? resetForgottenPassword() : sendCodeAndGoToOtp());

  const submitPassword = async (password: string) => {
    loading.value = true;

    const { flow, hasMatchedInvite, accountCreated, error } = await loginWithPassword({
      roleType,
      email: authState.value.email,
      password,
      ...(selectedEntity ? { selectedEntity } : {}),
    });

    if (!flow) {
      loading.value = false;
      return { flow: null, error };
    }

    if (flow === LoginFlowType.SignInExpired) {
      loading.value = false;
      restartSignIn(t('schools.auth.signInExpired'));
      return { flow, error: null };
    }

    if (flow === LoginFlowType.InvalidCredentials) {
      $analytics.track(EVENTS.LOGIN_PASSWORD_SUBMITTED, { correct: 'no' });
      loading.value = false;
      return { flow, error: null };
    }

    $analytics.track(EVENTS.LOGIN_PASSWORD_SUBMITTED, { correct: 'yes' });

    return await continueSignIn({ flow, hasMatchedInvite, accountCreated }, AuthAnalyticsMethod.EmailPassword);
  };

  return {
    chooseOtpMethod,
    choosePasswordMethod,
    forgotPassword,
    submitPassword,
  };
};
